// src/pages/integrations/CredentialCreateModal.jsx — SOUND-709
//
// One create modal, three surfaces, progressively less to ask:
//
//   /credentials              nothing known  -> choose client, DBA, MID
//   /companies/:id?tab=creds  client known   -> choose DBA, MID
//   /mids/:id                 all known      -> choose nothing
//
// GROUPED, NOT A FLAT LIST. The first cut asked ten questions in one column with
// DBA at the top, so the form opened on a narrowing filter for a thing the user
// had not chosen yet. Order now follows how someone actually thinks about it:
// what is this, whose is it, how do you get in.
//
// A field whose answer is already known is shown as CONTEXT, never as a disabled
// input — a greyed-out select still reads as a decision someone could have made
// differently.
import React, { useState, useEffect, useMemo } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { secretFieldsFor, looksLikeSecret } from "utilities/credentialFields";
import { X } from "lucide-react";
import CompanyPickerField from "components/CompanyPickerField";

const KINDS = [
  { key: "portal", label: "Portal login", hint: "A human signs into an acquirer or gateway portal." },
  { key: "api", label: "API credential", hint: "A machine uses it — the pipeline, or an integration." },
];

function Section({ title, subtitle, children }) {
  return (
    <section className="space-y-3">
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

export default function CredentialCreateModal({
  companyId: fixedCompanyId = null,
  midId: fixedMidId = null,
  contextLabel = null,
  onClose,
  onCreated,
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const auth = useMemo(() => ({ id: user?.id, accessToken: user?.accessToken }), [user?.id, user?.accessToken]);
  // CompanyHeader is WHO IS ASKING, always the signed-in user's company. The
  // company being asked ABOUT travels in the query string or the body.
  // Conflating the two sent an empty header on the global surface and every
  // request 400'd with "CompanyHeader is required".
  const callerCompanyId = user?.companyId;

  const [kind, setKind] = useState("portal");
  const [provider, setProvider] = useState("");
  const [label, setLabel] = useState("");
  const [companyId, setCompanyId] = useState(fixedCompanyId || "");
  const [dbaId, setDbaId] = useState("");
  const [midId, setMidId] = useState(fixedMidId || "");
  const [portal, setPortal] = useState({ url: "", username: "", has2fa: false, notes: "" });
  const [secrets, setSecrets] = useState({});
  // vault.fields was always an arbitrary named list. The registry offers what a
  // provider is KNOWN to use; this is for the case it does not cover yet, so a
  // new field does not require a deploy or get crammed into "password".
  const [extraFields, setExtraFields] = useState([]);
  const [saving, setSaving] = useState(false);

  // A failed fetch must not look like an empty list. RecordTable takes an
  // `error` prop for exactly this reason; a silent catch here made "9 providers
  // exist but the request failed" render as "No providers registered yet".
  const [providers, setProviders] = useState([]);
  const [providersError, setProvidersError] = useState(null);
  const [targets, setTargets] = useState({ dbas: [], mids: [] });
  const [targetsError, setTargetsError] = useState(null);

  useEffect(() => {
    makeApiRequest("get", apiRoutes.credentialProviders, auth, callerCompanyId)
      .then((res) => { setProviders(res.data?.data ?? []); setProvidersError(null); })
      .catch((e) => setProvidersError(e.response?.data?.error || e.message));
  }, [auth, callerCompanyId]);


  const effectiveCompanyId = fixedCompanyId || companyId;
  useEffect(() => {
    if (fixedMidId || !effectiveCompanyId) { setTargets({ dbas: [], mids: [] }); return; }
    makeApiRequest("get", apiRoutes.credentialLinkTargets(effectiveCompanyId), auth, callerCompanyId)
      .then((res) => { setTargets(res.data?.data ?? { dbas: [], mids: [] }); setTargetsError(null); })
      .catch((e) => setTargetsError(e.response?.data?.error || e.message));
  }, [auth, effectiveCompanyId, fixedMidId, callerCompanyId]);

  const secretFields = secretFieldsFor({ kind, provider, providerRegistry: providers });
  const notesLookSecret = looksLikeSecret(portal.notes);
  const visibleMids = targets.mids.filter((m) => !dbaId || m.dbaId === dbaId);
  const canSubmit = provider && label.trim() && effectiveCompanyId && !saving;

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const filled = Object.fromEntries(Object.entries(secrets).filter(([, v]) => v));
      const res = await makeApiRequest("post", apiRoutes.createCredential, auth, callerCompanyId, {
        companyId: effectiveCompanyId,
        provider,
        label: label.trim(),
        credentialType: kind,
        visibility: "internal",
        ...(midId && { midId }),
        ...(kind !== "api" && { portal }),
        // Empty values are dropped: a blank SecureString reads as "stored"
        // everywhere in the UI while holding nothing.
        ...(Object.keys(filled).length && { secrets: filled }),
      });
      toast({ title: "Credential created" });
      onCreated?.(res.data?.data ?? null);
      onClose();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Could not create credential",
        description: err.response?.data?.error || err.message,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-6" onClick={onClose}>
      <div className="surface my-auto w-full max-w-xl rounded-xl p-6 shadow-lg ring-1 ring-border" onClick={(e) => e.stopPropagation()}>
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Add credential</h3>
          <button className="btn btn-ghost h-8 w-8 p-0" onClick={onClose} aria-label="Close"><X className="h-4 w-4" /></button>
        </div>

        {contextLabel && (
          <p className="mb-5 rounded border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">{contextLabel}</p>
        )}

        <form onSubmit={submit} className="space-y-6">
          <Section title="What is it">
            <div className="grid grid-cols-2 gap-2">
              {KINDS.map((k) => (
                <button key={k.key} type="button" onClick={() => setKind(k.key)}
                  className={`btn ${kind === k.key ? "btn-primary" : "btn-secondary"}`}>
                  {k.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{KINDS.find((k) => k.key === kind)?.hint}</p>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">Provider</span>
                <select className="select w-full" value={provider} onChange={(e) => setProvider(e.target.value)} required>
                  <option value="">Select…</option>
                  {providers.map((p) => <option key={p.key} value={p.key}>{p.label || p.key}</option>)}
                </select>
                {providersError ? (
                  <p className="mt-1 text-xs text-destructive">Couldn’t load providers — {providersError}</p>
                ) : !providers.length ? (
                  <p className="mt-1 text-xs text-muted-foreground">No providers registered yet.</p>
                ) : null}
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">Label</span>
                <input className="input w-full" value={label} onChange={(e) => setLabel(e.target.value)}
                  placeholder={kind === "portal" ? "Merchant Portal" : "API Credentials"} required />
              </label>
            </div>
          </Section>

          <Section title="Whose is it" subtitle="The client that owns this credential. Linking a MID is optional.">
            {!fixedCompanyId && (
              // A plain <select> here silently truncated: GET /companies caps
              // limit at 100 and prod has 373 clients, so the last 273 were
              // simply absent with nothing saying so. CompanyPickerField
              // searches server-side, which is the only shape that stays correct
              // as the client list grows.
              <CompanyPickerField
                label="Client"
                value={companyId}
                onChange={({ id }) => { setCompanyId(id || ""); setDbaId(""); setMidId(""); }}
              />
            )}

            {!fixedMidId && effectiveCompanyId && (
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1 block text-xs text-muted-foreground">DBA</span>
                  <select className="select w-full" value={dbaId} onChange={(e) => { setDbaId(e.target.value); setMidId(""); }}>
                    <option value="">All DBAs</option>
                    {targets.dbas.map((d) => <option key={d.dbaId} value={d.dbaId}>{d.dbaName}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs text-muted-foreground">MID</span>
                  <select className="select w-full" value={midId} onChange={(e) => setMidId(e.target.value)}>
                    <option value="">Not MID-specific</option>
                    {visibleMids.map((m) => (
                      <option key={m.midId} value={m.midId}>
                        {[m.descriptor, m.processorName, m.mid].filter(Boolean).join(" · ")}
                      </option>
                    ))}
                  </select>
                </label>
                {targetsError && <p className="col-span-2 text-xs text-destructive">Couldn’t load DBAs or MIDs — {targetsError}</p>}
                {!targetsError && !targets.mids.length && (
                  <p className="col-span-2 text-xs text-muted-foreground">This client has no MIDs yet.</p>
                )}
              </div>
            )}
          </Section>

          <Section title={kind === "portal" ? "How you get in" : "Secrets"}>
            {kind !== "api" && (
              <div className="grid grid-cols-2 gap-3">
                <label className="col-span-2 block">
                  <span className="mb-1 block text-xs text-muted-foreground">Portal URL</span>
                  <input className="input w-full" value={portal.url} onChange={(e) => setPortal({ ...portal, url: e.target.value })} placeholder="https://…" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs text-muted-foreground">Username</span>
                  <input className="input w-full" autoComplete="off" value={portal.username} onChange={(e) => setPortal({ ...portal, username: e.target.value })} />
                </label>
                <label className="flex items-end gap-2 pb-2 text-sm">
                  <input type="checkbox" checked={portal.has2fa} onChange={(e) => setPortal({ ...portal, has2fa: e.target.checked })} />
                  Requires a 2FA code
                </label>
              </div>
            )}

            {secretFields.length || extraFields.length ? (
              <div className="grid grid-cols-2 gap-3">
                {[...secretFields, ...extraFields].map((f) => (
                  <label key={f} className="block">
                    <span className="mb-1 block text-xs capitalize text-muted-foreground">{f}</span>
                    <input type="password" autoComplete="new-password" className="input w-full"
                      value={secrets[f] || ""} onChange={(e) => setSecrets({ ...secrets, [f]: e.target.value })} />
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {provider ? "This provider declares no secret fields yet." : "Choose a provider to see its secret fields."}
              </p>
            )}

            {provider && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  const name = window.prompt("Field name (e.g. apiKey, securityKey)");
                  const clean = String(name || "").trim();
                  if (!clean) return;
                  if ([...secretFields, ...extraFields].includes(clean)) return;
                  setExtraFields((f) => [...f, clean]);
                }}
              >
                + Add another field
              </button>
            )}

            {kind !== "api" && (
              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">Notes</span>
                <textarea className="input w-full" rows={2} value={portal.notes} onChange={(e) => setPortal({ ...portal, notes: e.target.value })} />
                {notesLookSecret && (
                  <p className="mt-1 text-xs text-warning">
                    That looks like a secret. Put it in the password field above — notes are visible wherever
                    this credential is, and are not audit-logged when read.
                  </p>
                )}
              </label>
            )}
          </Section>

          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={!canSubmit}>{saving ? "Creating…" : "Create"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
